import {
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import OutsideListener from './OutsideListener'
import effects from '../effects/effects'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import {
  formatHomelessGroup,
  sortGroupByType,
  ORDINARY_GROUP,
} from 'commons/groupLogic'
import { compose, map, propOr } from 'ramda'

import {
  getHomelessTagIds,
  ifSelectSpecialToEmpty,
} from 'app/componentV4/GroupSelect/helpers'

const useStyle = makeStyles(() => ({
  inputSelectForm: {
    width: '100%',
    border: '1px solid #cbcbcb',
    borderRadius: 4,
  },
  tagsSelectOutlined: {
    width: '100%',
    borderBottom: '1px solid #e5e5e5',
    '& .selectTagText': {
      padding: '13px 3px !important',
      color: 'rgba(0, 0, 0, 0.54) !important',
    },
  },
  tagsSelectLabel: {
    background: 'white',
    padding: '0 4px',
    top: '-26px',
    fontSize: '10px',
    transform: 'translate(14px, 20px) scale(1) !important',
    color: 'rgba(0, 0, 0, 0.54) !important',
  },
  tagsSelect: {
    padding: '18.5px 14px',
  },
  tagsSelectValues: {
    width: 'auto !important',
    position: 'absolute',
    bottom: '-1px',
    transform: 'translateY(100%)',
    left: '0',
    right: '0',
    zIndex: '100',
  },
  tagsSelectListContainer: {
    overflow: 'auto',
    maxHeight: '160px',
  },
  fullWidth: {
    width: '100%',
  },
}))

export default (props) => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const fetchGroups = async () => {
    try {
      const groups = compose(
        map(formatHomelessGroup(formatMessage)),
        propOr([], 'data'),
      )(
        await effects.apis.groupsWithTags({
          limit: 10000,
          types: [ORDINARY_GROUP],
        }),
      )
      setState({
        ...state,
        text: generateText(state.selectedTags, groups),
        groups,
      })
    } catch (e) {
      dispatch(
        MessageAlert.showError({
          message: formatMessage({ id: 'wrong.msg' }),
        }),
      )
    }
  }
  let specialTypeId = {}

  const {
    isDisplayLabel = true,
    selectElemText,
    selectedTags,
    onTagsChange,
    disabled,
    customVariantOutlined = false,
  } = props
  const classes = useStyle()

  const generateText = (selectedTags, groups = state.groups) => {
    let text
    const allTagNames = []
    groups.forEach((group) => {
      group.tags &&
        group.tags.forEach((tag) => {
          selectedTags.forEach((tagId) => {
            if (tag.id === tagId) {
              allTagNames.push(tag.name)
            }
          })
        })
    })

    text = allTagNames.splice(0, 2).join(', ')

    if (allTagNames.length) {
      text +=
        ' ' +
        formatMessage({ id: 'label.and.more' }) +
        ' ' +
        allTagNames.length +
        ' items'
    }

    if (!text) {
      text = selectElemText || formatMessage({ id: 'group.select.selectGroup' })
    }

    return text
  }

  const [state, setState] = useState({
    isOpenPaper: false,
    text: selectElemText || formatMessage({ id: 'group.select.selectGroup' }),
    selectedTags: [...selectedTags],
    groups: [],
  })

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchGroups()
  }, [])
  /* eslint-enable react-hooks/exhaustive-deps */

  const isGroupChecked = (id) => {
    const currentGroup = state.groups.find((group) => group.id === id)
    let isChecked = true
    currentGroup.tags.forEach((tag) => {
      isChecked = isChecked && state.selectedTags.indexOf(tag.id) !== -1
    })
    return isChecked
  }

  const handleGroupCheck = (id) => {
    const tagIds = state.groups
      .find((group) => group.id === id)
      .tags.map((tag) => tag.id)

    if (isGroupChecked(id)) {
      let ids = state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1)

      setState({
        ...state,
        selectedTags: ids,
        text: generateText(
          state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1),
        ),
      })

      onTagsChange(ids)
    } else {
      let ids = []

      if (specialTypeId === id) {
        ids = [...new Set([...tagIds])]
      } else {
        /**/
        const selectedTags = ifSelectSpecialToEmpty(
          getHomelessTagIds(specialTypeId, state),
          state,
        )
        /**/

        ids = [...new Set([...selectedTags, ...tagIds])]
      }

      let generatedText = generateText(ids)

      setState({
        ...state,
        selectedTags: ids,
        text: generatedText,
      })
      onTagsChange(ids)
    }
  }

  const handleChange = (id) => {
    let newSelectedTags
    if (state.selectedTags.indexOf(id) !== -1) {
      newSelectedTags = state.selectedTags.filter((_id) => _id !== id)
      setState({
        ...state,
        text: generateText(newSelectedTags),
        selectedTags: newSelectedTags,
      })
    } else {
      const selectedTags = ifSelectSpecialToEmpty(
        getHomelessTagIds(specialTypeId, state),
        state,
      )

      newSelectedTags = [...selectedTags, id]
      let labelNames = generateText(newSelectedTags)

      setState({
        ...state,
        text: labelNames,
        selectedTags: newSelectedTags,
      })
    }
    onTagsChange(newSelectedTags)
  }

  const setPaperState = (status) => {
    if (disabled) return
    setState({
      ...state,
      isOpenPaper: status,
    })
  }

  return (
    <OutsideListener
      onClickOutside={() => setPaperState(false)}
      className={classes.fullWidth}
    >
      <FormControl
        variant="outlined"
        className={
          customVariantOutlined
            ? classes.tagsSelectOutlined
            : classes.inputSelectForm
        }
      >
        {isDisplayLabel && (
          <InputLabel
            className={classes.tagsSelectLabel}
            onClick={() => setPaperState(!state.isOpenPaper)}
            disableAnimation={true}
            disabled={true}
          >
            Group
          </InputLabel>
        )}
        <div
          className={clsx('selectTagText', classes.tagsSelect)}
          onClick={() => setPaperState(!state.isOpenPaper)}
        >
          {state.text}
        </div>
        {state.isOpenPaper && (
          <Paper elevation={1} className={classes.tagsSelectValues}>
            <div className={clsx('mx-16', classes.tagsSelectListContainer)}>
              {state.groups &&
                sortGroupByType(state.groups).map((group) => (
                  <div key={group.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGroupChecked(group.id)}
                          onChange={() => handleGroupCheck(group.id)}
                          value="checkedB"
                          color="primary"
                        />
                      }
                      label={group.name}
                    />
                    <div className="flex flex-col ltr:ml-16 rtl:mr-16">
                      {group.type !== 0
                        ? ((specialTypeId = group.id), '')
                        : group.tags &&
                          group.tags.map((tag) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    state.selectedTags.indexOf(tag.id) !== -1
                                  }
                                  onChange={() => handleChange(tag.id)}
                                  value="checkedB"
                                  color="primary"
                                />
                              }
                              label={tag.name}
                              key={tag.id}
                            />
                          ))}
                    </div>
                  </div>
                ))}
            </div>
          </Paper>
        )}
      </FormControl>
    </OutsideListener>
  )
}
